import { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  myautoiqTemplateGenerationSettingsActions,
} from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/slice';
import { watchMyautoiqTemplateGenerationSettingsSaga } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/saga';
import { selectMyautoiqTemplateGenerationSettings } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/selectors';
import { 
  fetchSegmentsForDealer,
  initializeMyautoiqTemplateGenerationSettings,
} from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/data/myautoiqTemplateGenerationSettingsDataService';
import { TemplateGenerationModal } from 'app/pages/TemplateGenerationSettingsComponent/components/TemplateGenerationModal';
import type { SegmentOption } from 'app/pages/TemplateGenerationSettingsComponent/components/TemplateGenerationModal';
import { TemplateGenerationTable } from 'app/components/ReusableUIComponents/TemplateGenerationTable';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';

import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';
import { ConfigureInitializeState } from 'app/components/ReusableUIComponents/ConfigureInitializeState';

import {
  Typography,
  Button,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

export interface EmailTemplateSegmentConfig {
  segment_id: string;
  segment_name: string;
  allowed_message_types: string[];
  auto_approval: boolean;
}

function getSettingsFromResponse(data: any): {
  settingsId: string | null;
  settingsDoc: any;
  emailTemplatesConfig: EmailTemplateSegmentConfig[];
} {
  const body = data?.body ?? data?.data ?? data;
  
  // Check if response is direct settings object (myautoiq-settings format)
  if (body && body._id && body.type === 'TEMPLATE_GENERATION_CONFIG') {
    const settingsId = body._id;
    const emailTemplatesConfig = body.properties?.email_templates_config ?? [];
    return { settingsId, settingsDoc: body, emailTemplatesConfig };
  }
  
  // Check if response is array format (dealer-settings format)
  if (body && Array.isArray(body) && body[0]?.settings?.length) {
    const settingsDoc = body[0].settings[0];
    const settingsId = settingsDoc?._id ?? null;
    const emailTemplatesConfig =
      settingsDoc?.properties?.email_templates_config ?? [];
    return { settingsId, settingsDoc, emailTemplatesConfig };
  }
  
  return { settingsId: null, settingsDoc: null, emailTemplatesConfig: [] };
}

interface MyautoiqTemplateGenerationSettingsComponentProps {
  manufacturerId: string;
  makeId?: string;
  makeName?: string;
  manufacturerName?: string;
}

export const MyautoiqTemplateGenerationSettingsComponent = ({
  manufacturerId,
  makeId,
}: MyautoiqTemplateGenerationSettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: watchMyautoiqTemplateGenerationSettingsSaga });

  const dispatch = useDispatch();
  const templateGenerationState = useSelector(selectMyautoiqTemplateGenerationSettings);
  const { data, loading, error } = templateGenerationState;

  const { settingsId, settingsDoc, emailTemplatesConfig } =
    getSettingsFromResponse(data);

  const [modalOpen, setModalOpen] = useState(false);
  const [editState, setEditState] = useState<{ edit: boolean; index: number }>({
    edit: false,
    index: -1,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [segmentsList, setSegmentsList] = useState<SegmentOption[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Check if response indicates an error (even with 200 status code)
  const hasError = useMemo(() => {
    if (!data) return false;
    const statusText = data?.statusText;
    const statusCode = data?.statusCode ?? data?.myAutoIQ_status_code ?? data?.errorCode;
    
    // If statusText is not "success" or status code is >= 400, it's an error
    return (statusText && statusText !== 'success') || (typeof statusCode === 'number' && statusCode >= 400);
  }, [data]);

  useEffect(() => {
    if (manufacturerId) {
      dispatch(
        myautoiqTemplateGenerationSettingsActions.requestMyautoiqTemplateGenerationSettings({
          manufacturerId: manufacturerId,
          makeId,
        }),
      );
    }
  }, [manufacturerId, makeId, dispatch]);

  useEffect(() => {
    if (modalOpen && !editState.edit && manufacturerId) {
      setSegmentsLoading(true);
      fetchSegmentsForDealer(manufacturerId)
        .then((res: any) => {
          const list = res?.body?.segments ?? [];
          setSegmentsList(Array.isArray(list) ? list : []);
        })
        .catch(() => setSegmentsList([]))
        .finally(() => setSegmentsLoading(false));
    }
  }, [modalOpen, editState.edit, manufacturerId]);

  const updateConfig = useCallback(
    (updatedConfig: EmailTemplateSegmentConfig[]) => {
      if (!settingsId || !settingsDoc || !manufacturerId) return;
      const email_templates_config = updatedConfig.map(row => {
        const { applicable_stages: _legacy, ...rest } = row as EmailTemplateSegmentConfig & {
          applicable_stages?: string[];
        };
        return rest;
      });
      const payload = {
        ...settingsDoc,
        properties: {
          ...(settingsDoc.properties || {}),
          email_templates_config,
        },
      };
      dispatch(
        myautoiqTemplateGenerationSettingsActions.requestUpdateMyautoiqTemplateGenerationSettings(
          {
            manufacturerId: manufacturerId,
            makeId,
            settingsId,
            data: payload,
          },
        ),
      );
    },
    [settingsId, settingsDoc, manufacturerId, makeId, dispatch],
  );

  const handleAddConfigurations = () => {
    setEditState({ edit: false, index: -1 });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditState({ edit: true, index });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditState({ edit: false, index: -1 });
  };

  const handleModalSave = (config: EmailTemplateSegmentConfig) => {
    const configs = [...emailTemplatesConfig];
    if (editState.edit && editState.index >= 0) {
      configs[editState.index] = config;
    } else {
      configs.push(config);
    }
    updateConfig(configs);
    handleModalClose();
  };

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex < 0) return;
    const configs = emailTemplatesConfig.filter((_, i) => i !== deleteIndex);
    updateConfig(configs);
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleAutoApprovalToggle = (segmentIndex: number, value: boolean) => {
    const config = [...emailTemplatesConfig];
    const segment = { ...config[segmentIndex], auto_approval: value };
    config[segmentIndex] = segment;
    updateConfig(config);
  };

  const handleInitialize = async () => {
    if (!manufacturerId || initializing) return;
    setInitializing(true);
    try {
      const response = await initializeMyautoiqTemplateGenerationSettings({
        manufacturerId,
        makeId,
      });
      
      // Check if initialization was successful
      const statusText = response?.statusText;
      if (statusText === 'success') {
        dispatch(notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Template generation settings initialized successfully',
        }));
        // Refetch data after successful initialization
        dispatch(
          myautoiqTemplateGenerationSettingsActions.requestMyautoiqTemplateGenerationSettings({
            manufacturerId: manufacturerId,
            makeId,
          }),
        );
      } else {
        throw new Error(response?.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      dispatch(notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error?.message || 'Failed to initialize template generation settings',
      }));
    } finally {
      setInitializing(false);
    }
  };

  const hasData = settingsId && settingsDoc;
  
  // Show initialize button when:
  // 1. Not loading AND manufacturer is selected
  // 2. AND (there's an error OR no data exists OR body is empty array)
  const isEmptyResponse = data && Array.isArray(data?.body) && data.body.length === 0;
  const showInitializeButton = !loading && manufacturerId && (hasError || !hasData || isEmptyResponse);

  return (
    <>
      <AccordionComponent
        elevation={0}
        data-guidance-tour="myautoiq-template-generation-settings"
        sx={{
          '&::before': { display: 'none' },
          padding: '16px',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '5px !important',
          marginBottom: '16px',
        }}
      >
        <AccordionSummaryComponent
          expandIcon={<ExpandMoreIcon />}
          aria-controls="myautoiq-template-generation-content"
          id="myautoiq-template-generation-header"
        >
          <Typography variant="h6" gutterBottom component="div">
            Template Generation Settings
          </Typography>
        </AccordionSummaryComponent>
        <AccordionDetailsComponent>
          {loading && (
            <Typography color="textSecondary">Loading...</Typography>
          )}
          {!manufacturerId && (
            <Typography color="textSecondary">
              Select a manufacturer to load template generation settings.
            </Typography>
          )}

          {showInitializeButton && (
            <ConfigureInitializeState
              warningMessage={hasError && data?.message ? data.message : null}
              errorMessage={error ? String(error) : null}
              message="No template generation config found. Click Initialize to set up."
              onInitialize={handleInitialize}
              initializing={initializing}
              buttonLabel="Initialize Template Generation Settings"
            />
          )}

          {!loading && hasData && !hasError && (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddConfigurations}
                style={{ marginTop: '25px', marginBottom: 16 }}
              >
                Add Configurations
              </Button>
              <TemplateGenerationTable
                emailTemplatesConfig={emailTemplatesConfig}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onAutoApprovalToggle={handleAutoApprovalToggle}
                ariaLabel="myautoiq template generation config"
              />
            </>
          )}
        </AccordionDetailsComponent>
      </AccordionComponent>

      <TemplateGenerationModal
        open={modalOpen}
        onClose={handleModalClose}
        mode={editState.edit ? 'edit' : 'add'}
        initialValues={
          editState.edit && editState.index >= 0
            ? emailTemplatesConfig[editState.index]
            : null
        }
        segments={segmentsList}
        segmentsLoading={segmentsLoading}
        onSave={handleModalSave}
        saving={loading}
      />

      <ConfirmationDialogMAIQ
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Template Generation config?"
        message="Are you sure you want to delete this configuration? This action cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};
